/* eslint-disable max-lines */

/**
 * Mastra `UIMessage` ↔ FE `ChatMessage` mapping.
 *
 * Mastra stores one `tool-invocation` part per call (args + result merged).
 * FE renders them as two parts (`tool-call` + `tool-result`) with a `status`
 * field so the UI can show "running" before the result lands. Sub-agents
 * (`agent-*` tools) also need their inner steps flattened into the parent
 * timeline. The mapper converts between the two shapes.
 *
 * - `toChatMessages` — Mastra rows → FE shape. Used on thread load.
 * - `toMastraUserMessage` — build one Mastra `UIMessage` for an outgoing user turn.
 * - `toMastraUIMessages` — FE shape → Mastra rows. Used for resume / replay.
 *
 * `subAgentMessages` (on `tool-call`) is FE-only and dropped on the round-trip back.
 */

import type { UIMessage } from '@ai-sdk/ui-utils';
import { z } from 'zod';

import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { generateUuid } from '@core/utils-legacy';

const MastraToolInvocationPartSchema = z
  .object({
    type: z.literal('tool-invocation'),
    toolInvocation: z.object({
      state: z.enum(['call', 'result', 'partial-call']),
      toolCallId: z.string(),
      toolName: z.string(),
      args: z.unknown(),
      result: z.unknown().optional(),
    }),
  })
  .passthrough();

const MastraTextPartSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
  createdAt: z.number().optional(),
});

const MastraReasoningPartSchema = z.object({
  type: z.literal('reasoning'),
  reasoning: z.string(),
});

const MastraRenderComponentPartSchema = z.object({
  type: z.literal('data-render-component'),
  data: z.object({
    componentId: z.string(),
    name: z.string(),
    props: z.unknown(),
  }),
});

const MastraSandboxToolCallPartSchema = z.object({
  type: z.literal('data-sandbox-tool-call'),
  data: z.object({
    callId: z.string(),
    id: z.string(),
    status: z.enum(['running', 'ok', 'error']),
    durationMs: z.number(),
  }),
});

const MastraProposeActionPartSchema = z.object({
  type: z.literal('data-propose-action'),
  data: z.object({
    proposalId: z.string(),
    action: z.string(),
    args: z.unknown(),
    display: z
      .object({
        summary: z.string().nullish(),
        done: z.string().nullish(),
        properties: z.array(z.object({ label: z.string(), value: z.string() })).nullish(),
      })
      .nullish(),
    confirm: z
      .object({
        title: z.string().optional(),
        summary: z.string().optional(),
        effect: z.string().optional(),
      })
      .passthrough()
      .nullish(),
    reversible: z.boolean().nullish(),
    risk: z.string().nullish(),
  }),
});

const MastraActionExecutedPartSchema = z.object({
  type: z.literal('data-action-executed'),
  data: z.object({
    executionId: z.string(),
    action: z.string(),
    args: z.unknown(),
    confirm: z
      .object({
        title: z.string().optional(),
        summary: z.string().optional(),
        effect: z.string().optional(),
      })
      .passthrough()
      .nullish(),
  }),
});

const MastraPartSchema = z.union([
  MastraToolInvocationPartSchema,
  MastraTextPartSchema,
  MastraReasoningPartSchema,
  MastraRenderComponentPartSchema,
  MastraSandboxToolCallPartSchema,
  MastraProposeActionPartSchema,
  MastraActionExecutedPartSchema,
  z.object({ type: z.string() }).passthrough(),
]);

const MastraMessageContentSchema = z.object({
  format: z.number().optional(),
  parts: z.array(MastraPartSchema).optional(),
  content: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});

const MastraMessageSchema = z.object({
  id: z.string().optional(),
  role: z.enum(['user', 'assistant', 'tool', 'system']),
  content: z.union([z.string(), MastraMessageContentSchema]).optional(),
  parts: z.array(MastraPartSchema).optional(),
  createdAt: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});

const SubAgentResultSchema = z.object({
  text: z.string().optional(),
  subAgentThreadId: z.string().optional(),
  subAgentResourceId: z.string().optional(),
  subAgentToolResults: z
    .array(
      z.object({
        toolCallId: z.string(),
        toolName: z.string(),
        args: z.unknown(),
        result: z.unknown(),
      }),
    )
    .optional(),
});

type SubAgentResult = z.infer<typeof SubAgentResultSchema>;

const SUB_AGENT_TOOL_NAME_PREFIX = 'agent-';

type MastraMessage = z.infer<typeof MastraMessageSchema>;

// UI events (e.g. action-outcome reports) travel as user messages so they
// persist and anchor a proper turn, but they are the UI talking to the
// agent — never shown in the transcript.
const messageMetadata = (m: MastraMessage): Record<string, unknown> | null =>
  m.metadata ?? (typeof m.content === 'object' ? (m.content?.metadata ?? null) : null);

const isUiEvent = (m: MastraMessage): boolean =>
  typeof messageMetadata(m)?.['uiEvent'] === 'string';

/** Convert Mastra DB rows to FE `ChatMessage[]`. Malformed rows are skipped silently. */
export const toChatMessages = (raw: readonly unknown[]): ChatMessage[] => {
  const out: ChatMessage[] = [];
  for (let i = 0; i < raw.length; i++) {
    const parsed = MastraMessageSchema.safeParse(raw[i]);
    if (!parsed.success) continue;
    const m = parsed.data;
    if (m.role !== 'user' && m.role !== 'assistant') continue;
    if (isUiEvent(m)) continue;
    const id = m.id ?? generateUuid();
    const parts =
      m.parts && m.parts.length > 0
        ? extractParts({ parts: m.parts }, id)
        : extractParts(m.content, id);
    if (parts.length === 0 && !(m.role === 'assistant' && i === raw.length - 1)) continue;
    out.push({
      id,
      role: m.role,
      parts,
      createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
    });
  }
  return out;
};

// eslint-disable-next-line max-lines-per-function
function extractParts(content: MastraMessage['content'], messageId: string): ChatPart[] {
  if (content === undefined) return [];
  if (typeof content === 'string') {
    return content ? [{ kind: 'text', messageId, content, done: true }] : [];
  }

  const parts: ChatPart[] = [];
  const seenToolCallIds = new Set<string>();

  for (const p of content.parts ?? []) {
    if (p.type === 'text') {
      const textPart = MastraTextPartSchema.safeParse(p);
      if (textPart.success && textPart.data.text) {
        parts.push({ kind: 'text', messageId, content: textPart.data.text, done: true });
      }
    } else if (p.type === 'reasoning') {
      const reasoningPart = MastraReasoningPartSchema.safeParse(p);
      if (reasoningPart.success && reasoningPart.data.reasoning) {
        parts.push({
          kind: 'reasoning',
          messageId,
          content: reasoningPart.data.reasoning,
          done: true,
        });
      }
    } else if (p.type === 'data-render-component') {
      const rcPart = MastraRenderComponentPartSchema.safeParse(p);
      if (rcPart.success) {
        parts.push({
          kind: 'render-component',
          componentId: rcPart.data.data.componentId,
          name: rcPart.data.data.name,
          props: rcPart.data.data.props,
        });
      }
    } else if (p.type === 'data-propose-action') {
      const paPart = MastraProposeActionPartSchema.safeParse(p);
      if (paPart.success) {
        parts.push({
          kind: 'propose-action',
          proposalId: paPart.data.data.proposalId,
          action: paPart.data.data.action,
          args: paPart.data.data.args,
          display: paPart.data.data.display
            ? {
                summary: paPart.data.data.display.summary ?? null,
                done: paPart.data.data.display.done ?? null,
                properties: paPart.data.data.display.properties ?? [],
              }
            : null,
          confirm: paPart.data.data.confirm ?? null,
          reversible: paPart.data.data.reversible ?? null,
          risk: paPart.data.data.risk ?? null,
        });
      }
    } else if (p.type === 'data-action-executed') {
      const aePart = MastraActionExecutedPartSchema.safeParse(p);
      if (aePart.success) {
        parts.push({
          kind: 'action-executed',
          executionId: aePart.data.data.executionId,
          action: aePart.data.data.action,
          args: aePart.data.data.args,
          confirm: aePart.data.data.confirm ?? null,
        });
      }
    } else if (p.type === 'data-sandbox-tool-call') {
      const sbPart = MastraSandboxToolCallPartSchema.safeParse(p);
      if (sbPart.success) {
        parts.push({
          kind: 'tool-call',
          toolCallId: sbPart.data.data.callId,
          name: sbPart.data.data.id,
          args: undefined,
          status: (<const>{ running: 'running', error: 'error', ok: 'done' })[
            sbPart.data.data.status
          ],
          startedAt: 0,
          durationMs: sbPart.data.data.durationMs,
          origin: 'sandbox',
        });
      }
    } else if (p.type === 'tool-invocation') {
      const toolPart = MastraToolInvocationPartSchema.safeParse(p);
      if (!toolPart.success) continue;
      const inv = toolPart.data.toolInvocation;
      if (seenToolCallIds.has(inv.toolCallId)) continue;
      seenToolCallIds.add(inv.toolCallId);

      const subAgentMessages = inv.toolName.startsWith(SUB_AGENT_TOOL_NAME_PREFIX)
        ? extractSubAgentMessages(p, inv)
        : undefined;

      parts.push({
        kind: 'tool-call',
        toolCallId: inv.toolCallId,
        name: inv.toolName,
        args: inv.args,
        status: inv.state === 'result' ? 'done' : 'running',
        startedAt: 0,
        ...(subAgentMessages !== undefined ? { subAgentMessages } : {}),
      });
      if (inv.state === 'result') {
        parts.push({
          kind: 'tool-result',
          toolCallId: inv.toolCallId,
          name: inv.toolName,
          payload: inv.result,
        });
      }
    }
  }

  if (parts.length === 0 && content.content) {
    parts.push({ kind: 'text', messageId, content: content.content, done: true });
  }

  return parts;
}

/** Build a Mastra UIMessage for a user-authored text turn. */
export const toMastraUserMessage = (
  text: string,
  metadata?: Record<string, unknown>,
): UIMessage => ({
  id: generateUuid(),
  role: 'user',
  content: text,
  parts: [{ type: 'text', text }],
  // Mastra copies UIMessage.metadata into the persisted content.metadata,
  // so UI events survive in thread memory as structured data.
  ...(metadata ? { metadata } : {}),
});

/**
 * System instructions must be sent in plain CoreMessage format ({ role, content }) —
 * adding UIMessage fields (`id`, `parts`) makes the server parse them as UIMessages
 * and reject the run.
 */
export interface MastraSystemMessage {
  role: 'system';
  content: string;
}

export const toMastraSystemMessage = (text: string): MastraSystemMessage => ({
  role: 'system',
  content: text,
});

export type MastraInputMessage = UIMessage | MastraSystemMessage;

type MastraUIPart = NonNullable<UIMessage['parts']>[number];
type MastraToolInvocationPart = Extract<MastraUIPart, { type: 'tool-invocation' }>;

interface MergedTool {
  toolName: string;
  args: unknown;
  result?: unknown;
  hasResult: boolean;
}

function mergeToolStates(parts: readonly ChatPart[]): ReadonlyMap<string, MergedTool> {
  const merged = new Map<string, MergedTool>();
  for (const part of parts) {
    if (part.kind === 'tool-call') {
      const prev = merged.get(part.toolCallId);
      merged.set(part.toolCallId, {
        toolName: part.name,
        args: part.args,
        result: prev?.result,
        hasResult: prev?.hasResult ?? false,
      });
    } else if (part.kind === 'tool-result') {
      const prev = merged.get(part.toolCallId);
      merged.set(part.toolCallId, {
        toolName: prev?.toolName ?? part.name,
        args: prev?.args ?? {},
        result: part.payload,
        hasResult: true,
      });
    }
  }
  return merged;
}

function toMastraToolPart(toolCallId: string, tool: MergedTool): MastraToolInvocationPart {
  const invocation: MastraToolInvocationPart['toolInvocation'] = tool.hasResult
    ? {
        state: 'result',
        toolCallId,
        toolName: tool.toolName,
        args: tool.args,
        result: tool.result,
      }
    : {
        state: 'call',
        toolCallId,
        toolName: tool.toolName,
        args: tool.args,
      };
  return { type: 'tool-invocation', toolInvocation: invocation };
}

function toMastraUIParts(parts: readonly ChatPart[]): MastraUIPart[] {
  const merged = mergeToolStates(parts);
  const firstSeen = new Map<string, number>();
  parts.forEach((part, index) => {
    const id = part.kind === 'tool-call' || part.kind === 'tool-result' ? part.toolCallId : null;
    if (id !== null && !firstSeen.has(id)) firstSeen.set(id, index);
  });

  return parts.flatMap((part, index): MastraUIPart[] => {
    if (part.kind === 'text') return [{ type: 'text', text: part.content }];
    if (part.kind === 'reasoning') {
      return [{ type: 'reasoning', reasoning: part.content, details: [] }];
    }
    if (part.kind === 'render-component') {
      // `data-render-component` is not in the Mastra UIMessage part union (custom data part);
      // cast through unknown so resume / replay round-trips the inline-render component.
      return [<MastraUIPart>(<unknown>{
          type: 'data-render-component',
          data: { componentId: part.componentId, name: part.name, props: part.props },
        })];
    }
    if (part.kind === 'propose-action') {
      // Custom data part — cast through unknown so resume / replay round-trips the proposal.
      return [<MastraUIPart>(<unknown>{
          type: 'data-propose-action',
          data: {
            proposalId: part.proposalId,
            action: part.action,
            args: part.args,
            display: part.display,
            confirm: part.confirm,
            reversible: part.reversible,
            risk: part.risk,
          },
        })];
    }
    if (part.kind === 'action-executed') {
      // Custom data part — cast through unknown so resume / replay round-trips the receipt.
      return [<MastraUIPart>(<unknown>{
          type: 'data-action-executed',
          data: {
            executionId: part.executionId,
            action: part.action,
            args: part.args,
            confirm: part.confirm,
          },
        })];
    }
    if (part.kind !== 'tool-call' && part.kind !== 'tool-result') return [];
    if (firstSeen.get(part.toolCallId) !== index) return [];
    if (part.kind === 'tool-call' && part.origin === 'sandbox') {
      return [<MastraUIPart>(<unknown>{
          type: 'data-sandbox-tool-call',
          data: {
            callId: part.toolCallId,
            id: part.name,
            status: part.status === 'error' ? 'error' : 'ok',
            durationMs: part.durationMs ?? 0,
          },
        })];
    }
    return [toMastraToolPart(part.toolCallId, merged.get(part.toolCallId)!)];
  });
}

/** Convert FE ChatMessage[] to Mastra UIMessage[] (used for resume / replay). */
export const toMastraUIMessages = (msgs: readonly ChatMessage[]): UIMessage[] =>
  msgs.map(m => {
    const partsOut = toMastraUIParts(m.parts);
    return {
      id: m.id,
      role: m.role,
      content: m.parts
        .filter((p): p is Extract<ChatPart, { kind: 'text' }> => p.kind === 'text')
        .map(p => p.content)
        .join(''),
      parts: partsOut,
    };
  });

function extractSubAgentMessages(
  rawPart: unknown,
  inv: { toolCallId: string; result?: unknown },
): ChatMessage[] | undefined {
  const live = (<{ subAgentBuffer?: { messages?: unknown[] } }>rawPart).subAgentBuffer;
  if (live && Array.isArray(live.messages) && live.messages.length > 0) {
    return toChatMessages(live.messages);
  }
  const parsed = SubAgentResultSchema.safeParse(inv.result);
  if (!parsed.success) return undefined;
  return [synthesizeSubAgentMessage(inv.toolCallId, parsed.data)];
}

function synthesizeSubAgentMessage(parentToolCallId: string, result: SubAgentResult): ChatMessage {
  const id = `${parentToolCallId}-sub`;
  const parts: ChatPart[] = [];
  for (const t of result.subAgentToolResults ?? []) {
    parts.push({
      kind: 'tool-call',
      toolCallId: t.toolCallId,
      name: t.toolName,
      args: t.args,
      status: 'done',
      startedAt: 0,
    });
    parts.push({
      kind: 'tool-result',
      toolCallId: t.toolCallId,
      name: t.toolName,
      payload: t.result,
    });
  }
  if (result.text) {
    parts.push({ kind: 'text', messageId: id, content: result.text, done: true });
  }
  return { id, role: 'assistant', parts, createdAt: new Date(0) };
}
